import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../../utils/apiClient';  // NEU
import { API_URL } from '../../config';

export const AppHeader = ({ onMenuToggle, menuOpen }) => {
  const [credits, setCredits] = useState(0);
  const [freeImages, setFreeImages] = useState(0);  // NEU
  const [freeVideos, setFreeVideos] = useState(0);
  const [user, setUser] = useState(null);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonateEmail, setImpersonateEmail] = useState('');

  useEffect(() => {
    // Impersonation-Check
    if (sessionStorage.getItem('snapshot_impersonation') === 'true') {
      setIsImpersonating(true);
      setImpersonateEmail(sessionStorage.getItem('snapshot_impersonate_email') || '');
    }

    // Load user from localStorage
    const userData = JSON.parse(localStorage.getItem('snapshot_user') || '{}');
    setUser(userData);
    const userId = userData.id;
    
    // Initial fetch
    const fetchCredits = () => {
      apiClient.get('/api/user/credits')
        .then(res => {
          if (!res.ok) throw new Error('Credits fetch failed');
          return res.json();
        })
        .then(data => {
          const newCredits = data.credits || 0;
          // Nur setzen wenn Wert gültig ist (verhindert Flackern)
          if (typeof newCredits === 'number' && newCredits >= 0) {
            setCredits(newCredits);
          }
        })
        .catch(err => {
          console.error('Error loading credits:', err);
          // Bei Fehler: Credits NICHT auf 0 setzen (behält alten Wert)
        });
    };
    
    const fetchFreeRewards = () => {
      apiClient.get('/api/auth/daily-claim-status')
        .then(res => {
          if (!res.ok) throw new Error('Free rewards fetch failed');
          return res.json();
        })
        .then(data => {
          setFreeImages(Number(data.free_images) || 0);
          setFreeVideos(Number(data.free_video_credits) || 0);
        })
        .catch(err => {
          console.error('Error loading free rewards:', err);
        });
    };

    const refreshHeaderStats = () => {
      fetchCredits();
      fetchFreeRewards();
    };
    
    // Fetch immediately
    if (userId) {
      refreshHeaderStats();
      
      // Auto-refresh every 30s (ETag-Revalidate → 304 bei unverändertem Stand)
      const interval = setInterval(() => {
        refreshHeaderStats();
      }, 30000);

      window.addEventListener('snapshot-user-updated', refreshHeaderStats);
      
      return () => {
        clearInterval(interval);
        window.removeEventListener('snapshot-user-updated', refreshHeaderStats);
      };
    } else {
      // Kein User → 0 Credits
      setCredits(0);
    }
  }, []);

  const handleExitImpersonation = () => {
    // Einfach sessionStorage leeren und Tab schließen
    // Admin-Tab hat seine eigene localStorage-Session und bleibt unangetastet
    sessionStorage.removeItem('snapshot_token');
    sessionStorage.removeItem('snapshot_user');
    sessionStorage.removeItem('snapshot_impersonation');
    sessionStorage.removeItem('snapshot_impersonate_email');
    sessionStorage.removeItem('snapshot_admin_token');
    sessionStorage.removeItem('snapshot_admin_user');
    window.close();
  };

  return (
    <div id="app-header" className="fixed top-0 left-0 right-0 z-40">
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="bg-orange-500 text-black px-4 py-1.5 flex items-center justify-between text-sm font-bold">
          <span>👁️ Eingeloggt als: {impersonateEmail}</span>
          <button
            onClick={handleExitImpersonation}
            className="flex items-center gap-1.5 px-3 py-1 bg-black/20 hover:bg-black/40 rounded-lg transition-all text-white text-xs font-bold"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Ausloggen
          </button>
        </div>
      )}
      <div className="bg-black/98 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-4xl mx-auto px-4 py-2.5">
        <div className="flex items-center justify-between">
          {/* Logo - Link zur ID-Card/Dashboard */}
          <Link to="/welcome" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center glow-green-soft">
              <svg className="w-4 h-4 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <span className="text-white font-bold text-sm">SNAPSHOT</span>
          </Link>

          {/* Right Side: SNAPs + Hamburger */}
          <div className="flex items-center gap-3">
            {/* SNAPs Badge - KLICKBAR */}
            <Link 
              to="/credits"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-full hover:bg-white/10 hover:border-[#10B981] transition-all cursor-pointer"
            >
              <div className="w-1.5 h-1.5 rounded-full bg-[#10B981] glow-green-soft"></div>
              <span className="text-white font-semibold text-xs">{Math.round(credits * 100) / 100} SNAPs</span>
            </Link>
            
            {/* Free Images Badge - nur wenn > 0 */}
            <div id="header-free-images-anchor" className="relative">
              {freeImages > 0 && (
                <Link
                  id="header-free-images-badge"
                  to="/credits"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-full hover:bg-emerald-500/20 hover:border-emerald-400 transition-all cursor-pointer"
                >
                  <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                  </svg>
                  <span className="text-emerald-400 font-semibold text-xs">{freeImages}</span>
                </Link>
              )}
            </div>

            <div id="header-free-videos-anchor" className="relative">
              {freeVideos > 0 && (
                <Link
                  id="header-free-videos-badge"
                  to="/credits"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-full hover:bg-fuchsia-500/20 hover:border-fuchsia-400 transition-all cursor-pointer"
                >
                  <svg className="w-3.5 h-3.5 text-fuchsia-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9A2.25 2.25 0 0015.75 16.5v-9A2.25 2.25 0 0013.5 5.25h-9A2.25 2.25 0 002.25 7.5v9A2.25 2.25 0 004.5 18.75z" />
                  </svg>
                  <span className="text-fuchsia-300 font-semibold text-xs">{freeVideos}</span>
                </Link>
              )}
            </div>
            
            {/* Hamburger Button - ANIMIERT */}
            {onMenuToggle && (
              <button
                onClick={onMenuToggle}
                className="p-2 hover:bg-white/10 rounded-lg transition-all"
                aria-label="Menu"
              >
                <div className="w-5 h-4 flex flex-col justify-between">
                  <span className={`w-full h-0.5 bg-white transition-all ${menuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
                  <span className={`w-full h-0.5 bg-white transition-all ${menuOpen ? 'opacity-0' : ''}`}></span>
                  <span className={`w-full h-0.5 bg-white transition-all ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
                </div>
              </button>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default AppHeader;
